import { useEffect, useRef, useState } from "react";
import type { ShareRole, Visibility } from "@hector/shared-types/resource-sharing";
import type { ShareGrant } from "./shareControlUtils";

// Ré-export pour les consommateurs qui importent `ShareGrant` depuis ce fichier.
export type { ShareGrant };

/**
 * Statut d'auto-enregistrement affiché par `ShareControl` : les modales de
 * partage persistent chaque changement immédiatement (pas de bouton
 * « Enregistrer ») — sans retour visuel l'utilisateur doute que ça a été
 * sauvegardé. `saving` pendant toute mutation en vol, `saved` ~1,5 s après
 * la dernière, puis `idle`.
 */
export type ShareSaveStatus = "idle" | "saving" | "saved";

/**
 * Entrées de `useShareController` : le point de branchement entre le contrôle
 * générique et les hooks TanStack Query propres à chaque ressource (dossier /
 * conversation / modèle). Chaque consommateur fournit ses propres
 * `useXShares`/`useAddXShare`/`useRemoveXShare`/`useUpdateXVisibility` — le
 * hook ne connaît ni caseId, ni conversationId, ni packId.
 */
export interface ShareControllerOps {
  /** Visibilité courante côté serveur (issue de la liste/détail de la ressource). */
  currentVisibility: Visibility;
  /** Ouverture de la modale hôte — resync la visibilité optimiste à l'ouverture. */
  open: boolean;
  /** Partages nominatifs courants (`undefined` tant que la query n'a pas résolu). */
  shares: ShareGrant[] | undefined;
  addShare: (args: { targetUserId: string; role: ShareRole }) => Promise<unknown>;
  removeShare: (userId: string) => Promise<unknown>;
  updateVisibility: (v: Visibility) => Promise<unknown>;
  updateVisibilityPending: boolean;
}

/** Sortie de `useShareController` — branchée directement sur les props `ShareControl`. */
export interface ShareController {
  visibility: Visibility;
  shares: ShareGrant[];
  pendingUserId: string | null;
  visibilityPending: boolean;
  onVisibilityChange: (v: Visibility) => void;
  onAddPerson: (userId: string) => void;
  onChangePersonRole: (userId: string, role: ShareRole) => void;
  onRemovePerson: (userId: string) => void;
  /** Retour d'auto-enregistrement (« Enregistrement… » / « Enregistré »). */
  saveStatus: ShareSaveStatus;
}

function sameShareGrants(left: ShareGrant[], right: ShareGrant[]): boolean {
  if (left.length !== right.length) return false;
  return left.every((share, index) => {
    const other = right[index];
    return other?.user_id === share.user_id && other.role === share.role;
  });
}

/**
 * Logique optimiste de partage, réutilisée par les 3 panneaux de partage
 * (dossier / conversation / modèle — `ShareCasePopover`/`ShareConversationPopover`/
 * `ShareTemplatePopover`). Isole tout l'état "en vol" (visibilité + user_id pending) du
 * composant présentationnel `ShareControl`, qui reste un pur contrôlé.
 *
 * Décision produit verrouillée : passer en visibilité `cabinet` place tout le
 * cabinet en ÉDITION (pas de variante cabinet lecture-seule) — ce hook ne fait
 * que relayer la valeur choisie par l'appelant, la contrainte est portée par
 * `ShareControl` (le picker n'offre que "Tout le cabinet", jamais un rôle).
 */
export function useShareController(ops: ShareControllerOps): ShareController {
  const { currentVisibility, open, shares, addShare, removeShare, updateVisibility, updateVisibilityPending } = ops;

  // État optimiste de la visibilité : le prop reflète la dernière donnée de
  // liste, mais le contrôle doit répondre immédiatement. Resynchronisé si le
  // prop change (ex. invalidation de liste après succès) ou à la réouverture.
  const [visibility, setVisibility] = useState<Visibility>(currentVisibility);
  useEffect(() => {
    setVisibility(currentVisibility);
  }, [currentVisibility, open]);

  // user_id en cours de mutation → désactive son contrôle (Select rôle ou
  // bouton retrait) le temps de l'aller-retour. Couvre aussi l'ajout : le
  // membre vient de disparaître du combobox, on ne veut pas d'un second clic
  // concurrent pendant que la query `shares` n'a pas encore été invalidée.
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [optimisticShares, setOptimisticShares] = useState<ShareGrant[]>(shares ?? []);
  const lastServerShares = useRef<ShareGrant[]>(shares ?? []);
  const lastOpen = useRef(open);
  useEffect(() => {
    const nextShares = shares ?? [];
    const openChanged = lastOpen.current !== open;
    if (!openChanged && sameShareGrants(lastServerShares.current, nextShares)) return;
    lastOpen.current = open;
    lastServerShares.current = nextShares;
    setOptimisticShares((current) => (sameShareGrants(current, nextShares) ? current : nextShares));
  }, [shares, open]);

  // Auto-enregistrement : `saving` tant qu'au moins une mutation est en vol
  // (compteur `inFlight` pour gérer des changements rapprochés), `saved` ~1,5 s
  // après la dernière, puis `idle`. Le timer est nettoyé au démontage et remis à
  // zéro à chaque ouverture/fermeture de la modale.
  const [saveStatus, setSaveStatus] = useState<ShareSaveStatus>("idle");
  const inFlight = useRef(0);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearSavedTimer = () => {
    if (savedTimer.current) {
      clearTimeout(savedTimer.current);
      savedTimer.current = null;
    }
  };
  const beginSave = () => {
    inFlight.current += 1;
    clearSavedTimer();
    setSaveStatus("saving");
  };
  const endSave = (ok: boolean) => {
    inFlight.current = Math.max(0, inFlight.current - 1);
    if (inFlight.current > 0) return; // d'autres mutations encore en vol
    if (ok) {
      setSaveStatus("saved");
      savedTimer.current = setTimeout(() => setSaveStatus("idle"), 1500);
    } else {
      // Échec : l'erreur est déjà signalée par le toast du hook mutation.
      setSaveStatus("idle");
    }
  };
  useEffect(() => () => clearSavedTimer(), []);
  // Reset à l'ouverture/fermeture UNIQUEMENT (pas sur currentVisibility, qui
  // change après un save réussi via l'invalidation de liste — sinon le « saved »
  // serait effacé instantanément).
  useEffect(() => {
    clearSavedTimer();
    inFlight.current = 0;
    setSaveStatus("idle");
  }, [open]);

  const onVisibilityChange = (next: Visibility) => {
    const previous = visibility;
    setVisibility(next); // optimiste
    beginSave();
    void (async () => {
      try {
        await updateVisibility(next);
        endSave(true);
      } catch {
        // rollback — le toast d'erreur est déjà émis par le hook mutation
        // appelant, ne pas re-toaster ici.
        setVisibility(previous);
        endSave(false);
      }
    })();
  };

  const onAddPerson = (userId: string) => {
    const previousShares = optimisticShares;
    // Le partage est visible immédiatement ; l'appel API ne sert qu'à confirmer
    // la persistance. Sans cet état local, l'utilisateur voyait une latence vide
    // jusqu'au refetch TanStack Query.
    setOptimisticShares((current) => [
      ...current.filter((share) => share.user_id !== userId),
      { user_id: userId, role: "editor" },
    ]);
    setPendingUserId(userId);
    beginSave();
    void (async () => {
      try {
        // Défaut produit : un partage nominatif donne l'édition. L'owner peut
        // ensuite rétrograder en lecture depuis la liste des partagés.
        await addShare({ targetUserId: userId, role: "editor" });
        endSave(true);
      } catch {
        setOptimisticShares(previousShares);
        endSave(false); // toast déjà émis par le hook mutation
      } finally {
        setPendingUserId(null);
      }
    })();
  };

  const onChangePersonRole = (userId: string, role: ShareRole) => {
    const previousShares = optimisticShares;
    setOptimisticShares((current) =>
      current.map((share) => (share.user_id === userId ? { ...share, role } : share)),
    );
    setPendingUserId(userId);
    beginSave();
    void (async () => {
      try {
        // Upsert : addShare change le rôle d'un partage existant.
        await addShare({ targetUserId: userId, role });
        endSave(true);
      } catch {
        setOptimisticShares(previousShares);
        endSave(false);
      } finally {
        setPendingUserId(null);
      }
    })();
  };

  const onRemovePerson = (userId: string) => {
    const previousShares = optimisticShares;
    setOptimisticShares((current) => current.filter((share) => share.user_id !== userId));
    setPendingUserId(userId);
    beginSave();
    void (async () => {
      try {
        await removeShare(userId);
        endSave(true);
      } catch {
        setOptimisticShares(previousShares);
        endSave(false); // toast déjà émis par le hook mutation
      } finally {
        setPendingUserId(null);
      }
    })();
  };

  return {
    visibility,
    shares: optimisticShares,
    pendingUserId,
    visibilityPending: updateVisibilityPending,
    onVisibilityChange,
    onAddPerson,
    onChangePersonRole,
    onRemovePerson,
    saveStatus,
  };
}
